import { AtomTagsProvider } from '@atoms/AtomTags/Context';
import { AutoPilotProvider } from '@common/autoPilot';
import { CodeGenStatusType } from '@common/enums';
import { IDEModeProvider } from '@common/ide/IdeMode';
import { IdeStateProvider, useIDE, useIdeActions } from '@common/ide/state';
import { PipelineRunInfoType } from '@common/queries/pipeline';
import { EditMode } from '@common/types';
import { Entity, IDEEntity } from '@common/types/Entity';
import { useCurrentGraph } from '@common/useOriginalGraph';
import { ViewType } from '@LSP/types';
import { AttachFabricProvider } from '@pages/Workflow/AttachFabric/AttachFabricProvider';
import { GraphProvider } from '@prophecy/ui-v3/Graph/GraphContext';
import { FROZEN_EMPTY_ARRAY, FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { useExecutionService } from 'frontend/core/src/data/executionHooks';
import { memo, useEffect } from 'react';

import { SchemaProviderContext } from '../../../schema/SchemaProvider';
import { ArrowTypeToSchemaTypeMap, ColumnToSchemaTypeMap, ProviderColumnTypes } from '../../../schema/sqlTypes';
import { Providers } from '../../../schema/types';
import WorkflowDatasetUploadProvider from '../../Workflow/Dataset/context';
import { useExecutionNotification } from '../../Workflow/hook';
import { useAtomTags } from '../../Workflow/hooks/useAtomTags';
import { MockProcessDialog } from './MockProcessDialog';

export const Container = memo(
  ({
    uid,
    projectId,
    projectName,
    language,
    branch
  }: {
    uid: string;
    projectId: string;
    projectName: string;
    language: 'scala' | 'python' | 'sql';
    branch: string;
    codeGenStatus?: CodeGenStatusType;
    pipelineNotFound?: boolean;
    initialize?: (viewType: ViewType, jobData?: PipelineRunInfoType) => Promise<void>;
  }) => {
    const ideActions = useIdeActions();
    const executionService = useExecutionService();

    const readOnly = false;
    useExecutionNotification(executionService, readOnly, false);

    useEffect(() => {
      ideActions.setReadOnly(readOnly);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ideActions]);

    const { getTagCategories, renderAtomTagLabel } = useAtomTags();
    const { setSourceSuggestions } = useIdeActions();
    useEffect(() => {
      return () => {
        setSourceSuggestions(undefined);
      };
    }, [setSourceSuggestions]);

    const { state } = useIDE({
      uid,
      entity: Entity.Pipeline as IDEEntity,
      projectId,
      projectName,
      language,
      branch,
      repositoryPath: '',
      path: ''
    });

    const pipelineId = uid;

    const { rootGraph } = useCurrentGraph();

    return (
      <IDEModeProvider value={EditMode.enabled}>
        <IdeStateProvider value={state}>
          <SchemaProviderContext.Provider
            value={{
              providerType: Providers.Spark,
              providerColumnTypes: FROZEN_EMPTY_ARRAY as ProviderColumnTypes,
              columnToSchemaTypeMap: FROZEN_EMPTY_OBJECT as ColumnToSchemaTypeMap,
              arrowTypeToSchemaTypeMap: FROZEN_EMPTY_OBJECT as ArrowTypeToSchemaTypeMap
            }}>
            <AttachFabricProvider allowFabricSelection={false}>
              <AtomTagsProvider getTagCategories={getTagCategories} renderAtomTagLabel={renderAtomTagLabel}>
                {/* graphView code */}
                <GraphProvider key={`graph-${pipelineId}`}>
                  {/* thsese are from graphView.tsx to enable using useGraph hook in mockProcessDialog */}
                  <AutoPilotProvider listenShiftKey={true}>
                    {rootGraph ? (
                      <WorkflowDatasetUploadProvider>
                        <MockProcessDialog />
                      </WorkflowDatasetUploadProvider>
                    ) : (
                      <MockProcessDialog />
                    )}
                  </AutoPilotProvider>
                </GraphProvider>
                {/* graphView code */}
              </AtomTagsProvider>
            </AttachFabricProvider>
          </SchemaProviderContext.Provider>
        </IdeStateProvider>
      </IDEModeProvider>
    );
  }
);
